import { useEffect, useState, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Users, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackPurchase } from "@/lib/analytics";

export default function SlotStore() {
  const { refreshUser } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [qty, setQty] = useState(1);

  const loadCfg = () =>
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => setCfg(false));
  useEffect(() => { loadCfg(); }, []);

  // Memoize PayPal options to prevent unnecessary provider remounts
  const paypalOptions = useMemo(() => {
    if (!cfg?.client_id) return null;
    return {
      clientId: cfg.client_id,
      currency: cfg.currency || "USD",
      intent: "capture",
      components: "buttons"
    };
  }, [cfg?.client_id, cfg?.currency]);

  if (cfg === null) return null;
  if (!cfg || !cfg.client_id || !cfg.extra_slot_price || !paypalOptions) return null;

  const owned = cfg.extra_slots || 0;
  const adminBonus = cfg.admin_bonus_slots || 0;
  const maxExtra = cfg.max_extra_slots || 5;
  const remaining = Math.max(0, maxExtra - owned - adminBonus);
  const price = Number(cfg.extra_slot_price);
  const discountPct = cfg.extra_slot_discount_pct || 0;
  const bulkDiscount = cfg.extra_slot_bulk_discount || 0;
  const extraUnit = price * (1 - discountPct / 100);
  const clampedQty = Math.min(Math.max(1, qty), Math.max(1, remaining));
  const isBulk = clampedQty >= maxExtra;
  const total = (isBulk
    ? price * maxExtra - bulkDiscount
    : price + Math.max(0, clampedQty - 1) * extraUnit).toFixed(2);
  const fullTotal = (price * clampedQty).toFixed(2);
  const savings = (fullTotal - total).toFixed(2);

  return (
    <div data-testid="slot-store" className="border border-sky-700/40 bg-gradient-to-br from-sky-950/20 to-zinc-950 p-6 mb-8">
      <div className="flex items-center gap-2 mb-1">
        <Users size={20} className="text-sky-400" />
        <h3 className="font-cinzel text-sky-300 uppercase tracking-widest text-xl">Extra Gladiator Slots</h3>
      </div>
      <p className="text-sm text-zinc-400 mb-1">
        Permanently expand your ludus — buy up to {maxExtra} extra gladiator slots at ${cfg.extra_slot_price} each.
      </p>
      {discountPct > 0 && (
        <p className="text-xs text-emerald-400 mb-1" data-testid="slot-discount-note">
          Buy more than one at a time and every additional slot is <span className="font-semibold">{discountPct}% off</span>
          {bulkDiscount > 0 && <> — or grab all {maxExtra} at once for just <span className="font-semibold">${(price * maxExtra - bulkDiscount).toFixed(2)}</span>!</>}
        </p>
      )}
      <p className="text-xs text-zinc-500 mb-5">
        Slots are permanent and stack with VIP. Your current roster cap is{" "}
        <span className="text-sky-300 font-semibold" data-testid="slot-current-cap">{cfg.roster_cap}</span> gladiators
        {(owned > 0 || adminBonus > 0) && (
          <span className="text-zinc-400" data-testid="slot-owned-detail"> ({[owned > 0 ? `${owned} purchased` : null, adminBonus > 0 ? `${adminBonus} awarded` : null].filter(Boolean).join(", ")})</span>
        )}.
      </p>

      {remaining === 0 ? (
        <div data-testid="slot-maxed" className="bg-zinc-950/60 border border-sky-900/40 p-4 rounded-sm max-w-sm text-sm text-sky-200">
          You have all {maxExtra} extra slots — your ludus is fully expanded!
        </div>
      ) : (
        <div className="bg-zinc-950/60 border border-sky-900/40 p-4 rounded-sm max-w-sm">
          <label className="text-[11px] uppercase tracking-widest text-zinc-500 block mb-2">How many slots?</label>
          <div className="flex items-center gap-3 mb-4">
            <button data-testid="slot-qty-minus" type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={clampedQty <= 1}
              className="w-9 h-9 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:border-sky-500 hover:text-sky-300 transition-colors rounded-sm disabled:opacity-40">
              <Minus size={15} />
            </button>
            <span data-testid="slot-qty" className="font-cinzel text-2xl text-sky-200 tabular-nums w-8 text-center">{clampedQty}</span>
            <button data-testid="slot-qty-plus" type="button" onClick={() => setQty((q) => Math.min(remaining, q + 1))} disabled={clampedQty >= remaining}
              className="w-9 h-9 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:border-sky-500 hover:text-sky-300 transition-colors rounded-sm disabled:opacity-40">
              <Plus size={15} />
            </button>
            <span className="text-xs text-zinc-500 ml-1">{remaining} available</span>
          </div>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3 text-center" data-testid="slot-selected-label">
            Pay <span className="text-emerald-300 font-semibold normal-case tracking-normal text-sm">${total}</span> for {clampedQty} permanent slot{clampedQty > 1 ? "s" : ""}
            {Number(savings) > 0 && (
              <span className="block mt-1 normal-case tracking-normal text-[11px] text-zinc-500">
                <span className="line-through" data-testid="slot-old-price">${fullTotal}</span>
              </span>
            )}
          </p>
          <PayPalScriptProvider options={paypalOptions}>
            <PayPalButtons
              key={clampedQty}
              style={{ layout: "vertical", color: "blue", shape: "pill", label: "pay" }}
              createOrder={async () => {
                const r = await api.post("/paypal/slots/create-order", { quantity: clampedQty });
                return r.data.order_id;
              }}
              onApprove={async (data) => {
                try {
                  const r = await api.post("/paypal/slots/capture", { order_id: data.orderID });
                  trackPurchase({ value: Number(cfg.extra_slot_price) * clampedQty, currency: cfg.currency || "USD", transactionId: data.orderID, itemType: "roster_slots" });
                  toast.success(`${r.data.amount} gladiator slot${r.data.amount > 1 ? "s" : ""} added! Your ludus now holds ${r.data.roster_cap}.`);
                  setQty(1);
                  await loadCfg();
                  await refreshUser();
                } catch (e) { toast.error(apiErr(e)); }
              }}
              onError={() => toast.error("PayPal error. Please try again.")}
            />
          </PayPalScriptProvider>
        </div>
      )}
    </div>
  );
}
